"use client";

import React from "react";
import { useFormik, type FormikErrors } from "formik";
import { MdPhoneIphone } from "react-icons/md";
import { clientLogger } from "@/lib/logger";
import { validatePhone } from "@/lib/utils";
import { getOtp } from "@/app/product/_actions/user.actions";
import { useAppData } from "@/store/appStore";
import { Logo } from "@/design-elements/logo";
import { trackEvent, GTMEvent } from "@/lib/utils/gtm-events";

interface StepMobileProps {
	onContinue: (mobile: string) => void;
	onSkip?: () => void;
	initialMobile?: string;
}

interface FormValues {
	mobile: string;
}

const MOBILE_COPY = {
	heroTitle: "What's your mobile number?",
	heroSubtitle:
		"Help us verify your account and be\nable to assist you faster when needed",
	label: "Mobile Number",
	placeholder: "9876543210",
	submit: "Send OTP",
	submitting: "Sending...",
	skip: "Skip",
	errorInvalidMobile: "Please enter a valid 10-digit mobile number.",
	logSubmit: "Onboarding: Mobile submitted",
	logSkip: "Onboarding: Mobile step skipped",
};

const StepMobile: React.FC<StepMobileProps> = ({
	onContinue,
	onSkip,
	initialMobile = "",
}) => {
	const [, setAppData] = useAppData();

	const formik = useFormik<FormValues>({
		initialValues: { mobile: initialMobile },
		validate: (values) => {
			const errors: FormikErrors<FormValues> = {};
			if (!validatePhone(values.mobile || "")) {
				errors.mobile = MOBILE_COPY.errorInvalidMobile;
			}
			return errors;
		},
		onSubmit: async (values, { setSubmitting, setFieldError }) => {
			try {
				const response = await getOtp(values.mobile, "mobileNo");

				if (!response?.error) {
					setAppData({ phoneNumber: Number(values.mobile) });
					clientLogger.info(MOBILE_COPY.logSubmit, { mobile: values.mobile });
					onContinue(values.mobile);
					setFieldError("mobile", undefined);
				} else {
					setFieldError(
						"mobile",
						(response?.errorText as string) || MOBILE_COPY.errorInvalidMobile,
					);
					setSubmitting(false);
				}
			} catch (err) {
				clientLogger.error("Error submitting mobile:", err as Error);
				setFieldError(
					"mobile",
					err instanceof Error ? err.message : MOBILE_COPY.errorInvalidMobile,
				);
				setSubmitting(false);
			}
		},
	});

	const handleSkip = () => {
		formik.resetForm();
		trackEvent(GTMEvent.MOBILE_VERIFICATION_SKIPPED);
		clientLogger.info(MOBILE_COPY.logSkip);
		if (onSkip) {
			onSkip();
		}
	};

	const mobileError =
		formik.touched.mobile && formik.errors.mobile
			? formik.errors.mobile
			: undefined;
	const submitDisabled =
		formik.isSubmitting || !validatePhone(formik.values.mobile || "");
	const showSubmitDisabledStyle = submitDisabled && !formik.isSubmitting;

	return (
		<section className="relative flex flex-col items-stretch w-full h-full bg-[var(--automint-onboarding-bg)] text-[var(--automint-black)] md:bg-[var(--automint-black)] md:text-white rounded-[6px] overflow-hidden">
			<div className="flex flex-col flex-1 justify-center md:justify-start px-5 md:px-7 py-6 md:py-8">
				<div className="mx-auto">
					<Logo
						showText={true}
						invert
						size={40}
						className="[&_span]:!text-[var(--automint-black)] md:[&_span]:!text-white [&_span]:!text-2xl [&_span]:!font-medium"
					/>
				</div>

				<div className="flex flex-col md:flex-1 md:justify-center">
					<div className="mt-14 md:mt-0 2xl:mt-0 text-center">
						<h1 className="text-[26px] md:text-[28px]  leading-tight">
							{MOBILE_COPY.heroTitle}
						</h1>
						<p className="text-sm text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)] mt-3 whitespace-pre-line">
							{MOBILE_COPY.heroSubtitle}
						</p>
					</div>

					<div className="mt-6">
						<label
							htmlFor="onboarding_mobile_input"
							className="block text-sm text-[var(--automint-black)] md:text-white mb-2"
						>
							{MOBILE_COPY.label}
						</label>
						<div className="relative">
							<MdPhoneIphone
								className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)]"
								size={18}
							/>
							<input
								id="onboarding_mobile_input"
								name="mobile"
								type="tel"
								inputMode="numeric"
								autoComplete="tel"
								autoFocus
								placeholder={MOBILE_COPY.placeholder}
								value={formik.values.mobile}
								onChange={(e) => {
									const digitsOnly = e.target.value
										.replace(/\D/g, "")
										.slice(0, 10);
									formik.setFieldValue("mobile", digitsOnly, true);
									if (!formik.touched.mobile) {
										formik.setFieldTouched("mobile", true, false);
									}
								}}
								onBlur={formik.handleBlur}
								disabled={formik.isSubmitting}
								onKeyDown={(e) => {
									if (e.key === "Enter" && !submitDisabled) {
										formik.handleSubmit();
									}
								}}
								className="w-full h-12 rounded-[6px] bg-[var(--colors-automint-neutral-200)] border border-[var(--colors-automint-neutral-200)] text-[var(--automint-black)] placeholder:text-[var(--colors-automint-neutral-500)] md:bg-[var(--colors-automint-neutral-800)] md:border-[var(--colors-automint-neutral-800)] md:text-white md:placeholder:text-[var(--automint-onboarding-placeholder-dark)] pl-11 pr-4 text-[15px] focus:outline-none focus:border-[var(--automint-black)] md:focus:border-[var(--automint-onboarding-focus-border-dark)] disabled:opacity-60"
							/>
						</div>
						{mobileError && (
							<p className="text-[var(--automint-error-text-light)] md:text-[var(--automint-error-text-dark)] text-xs mt-2">
								{mobileError}
							</p>
						)}
					</div>

					<div className="mt-5">
						<button
							id="onboarding_mobile_continue"
							type="button"
							onClick={() => formik.handleSubmit()}
							disabled={submitDisabled}
							className={`w-full h-12 rounded-[6px] text-center font-medium text-sm leading-normal transition-colors disabled:cursor-not-allowed bg-[var(--automint-black)] text-[var(--colors-automint-neon-500)] md:bg-[var(--colors-automint-neon-500)] md:text-[var(--automint-black)] ${showSubmitDisabledStyle ? "opacity-15 md:opacity-100 md:!bg-[var(--automint-onboarding-disabled-bg)] md:!text-[var(--automint-onboarding-disabled-text)]" : ""}`}
						>
							{formik.isSubmitting
								? MOBILE_COPY.submitting
								: MOBILE_COPY.submit}
						</button>
					</div>

					{onSkip && (
						<div className="mt-4 mb-2 text-center">
							<button
								id="onboarding_mobile_skip"
								type="button"
								onMouseDown={(e) => e.preventDefault()}
								onClick={handleSkip}
								className="text-[var(--automint-black)] md:text-white text-sm underline underline-offset-4 hover:opacity-80"
							>
								{MOBILE_COPY.skip}
							</button>
						</div>
					)}
				</div>
			</div>
		</section>
	);
};

export default StepMobile;
